import { pxToRem } from '@withjoy/joykit/theme';
import React, { useState, useEffect, useRef } from 'react';
import { Box, CheckboxV2, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import { Close, Info } from '@withjoy/joykit/icons';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTimeout } from '@shared/hooks/useTimeout';

type ShowAvailableOnlyFacetProps = Readonly<{
  isChecked: boolean;
  onToggle: (event: React.ChangeEvent<HTMLInputElement>) => void;
  title: string;
  tooltipContent?: React.ReactNode;
  showTooltipOnMount?: boolean;
  isPanelVisible: boolean;
}>;

const TOOLTIP_DELAY_MS = 1000;
const TOOLTIP_AUTO_DISMISS_MS = 5000;

export const ShowAvailableOnlyFacet: React.FC<ShowAvailableOnlyFacetProps> = ({ title, onToggle, isChecked, tooltipContent, showTooltipOnMount = false, isPanelVisible }) => {
  const hasShownTooltipRef = useRef(false);
  const wasAutoOpenedRef = useRef(false);
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const { set: setAutoOpenTimer, clear: clearAutoOpenTimer } = useTimeout();
  const { set: setAutoDismissTimer, clear: clearAutoDismissTimer } = useTimeout();

  useEffect(() => {
    if (!tooltipContent || !showTooltipOnMount || hasShownTooltipRef.current || !isPanelVisible) {
      return;
    }

    setAutoOpenTimer(() => {
      hasShownTooltipRef.current = true;
      wasAutoOpenedRef.current = true;
      setTooltipIsOpen(true);
    }, TOOLTIP_DELAY_MS);

    return () => clearAutoOpenTimer();
  }, [tooltipContent, showTooltipOnMount, isPanelVisible, setAutoOpenTimer, clearAutoOpenTimer]);

  // Auto-dismiss only when tooltip was programmatically opened (not from hover)
  useEffect(() => {
    if (!tooltipIsOpen || !wasAutoOpenedRef.current) return;

    setAutoDismissTimer(() => {
      wasAutoOpenedRef.current = false;
      setTooltipIsOpen(false);
    }, TOOLTIP_AUTO_DISMISS_MS);

    return () => clearAutoDismissTimer();
  }, [tooltipIsOpen, setAutoDismissTimer, clearAutoDismissTimer]);

  const handleToggle = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTooltipIsOpen(false);
    onToggle(event);
  });

  const handleInfoIconClick = useEventCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    wasAutoOpenedRef.current = false;
    setTooltipIsOpen(prev => !prev);
  });

  const handleTooltipClose = useEventCallback(() => {
    wasAutoOpenedRef.current = false;
    setTooltipIsOpen(false);
  });

  return (
    <Flex flexDirection="column">
      <CheckboxV2
        isChecked={isChecked}
        onChange={handleToggle}
        id="show-available-only"
      >
        <Flex
          alignItems="center"
          gap={pxToRem(3)}
        >
          <TextV2
            color="mono14"
            typographyVariant="body1"
          >
            {title}
          </TextV2>
          {tooltipContent && (
            <Box
              as="button"
              onClick={handleInfoIconClick}
              display="inline-flex"
              aria-label="More information"
              __css={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              <TooltipV2
                triggerType="hover"
                // `undefined` delegates to TooltipV2's internal hover state; `true` forces open (auto-show on mount)
                isOpen={tooltipIsOpen || undefined}
                onClose={handleTooltipClose}
                overrides={{
                  Content: { props: { paddingY: 4, paddingLeft: pxToRem(14), paddingRight: 4, borderRadius: pxToRem(12), color: 'white', marginLeft: 2 } },
                }}
                content={
                  <Flex
                    maxWidth={pxToRem(230)}
                    alignItems="flex-start"
                    gap={2}
                  >
                    <Box
                      typographyVariant="label2"
                      flex={1}
                      textAlign="left"
                    >
                      {tooltipContent}
                    </Box>
                    {wasAutoOpenedRef.current && (
                      <Box
                        as="button"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          handleTooltipClose();
                        }}
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        aria-label="Close tooltip"
                        __css={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'white' }}
                      >
                        <Close size={16} />
                      </Box>
                    )}
                  </Flex>
                }
                placement="top"
              >
                <Info size={16} />
              </TooltipV2>
            </Box>
          )}
        </Flex>
      </CheckboxV2>
    </Flex>
  );
};
